import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Badge,
  Menu,
  MenuItem,
  Avatar,
  Box,
  InputBase,
  Divider,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getRoleLabel, getRoleColor } from '../../utils/permissions';

const PRIMARY_COLOR = '#1B365D';
const SECONDARY_COLOR = '#00B894';

const SearchWrapper = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: alpha('#1B365D', 0.06),
  '&:hover': {
    backgroundColor: alpha('#1B365D', 0.1),
  },
  marginRight: theme.spacing(2),
  marginLeft: theme.spacing(2),
  width: 'auto',
  maxWidth: 400,
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 1.5),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: alpha('#1B365D', 0.45),
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: PRIMARY_COLOR,
  fontSize: 14,
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(3)})`,
    transition: theme.transitions.create('width'),
    width: 200,
    '&::placeholder': {
      color: alpha('#1B365D', 0.5),
      opacity: 1,
    },
    [theme.breakpoints.down('md')]: {
      width: 140,
    },
  },
}));

const TopBar = ({ onMenuToggle, pageTitle = 'Dashboard' }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const profileMenuOpen = Boolean(anchorEl);
  const notifMenuOpen = Boolean(notifAnchorEl);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotifOpen = (event) => {
    setNotifAnchorEl(event.currentTarget);
  };

  const handleNotifClose = () => {
    setNotifAnchorEl(null);
  };

  const handleMenuAction = (action) => {
    handleProfileMenuClose();
    if (action === 'logout') {
      logout();
      navigate('/login');
    } else if (action === 'profile') {
      navigate('/profile');
    } else if (action === 'settings') {
      navigate('/profile');
    }
  };

  const userInitials = user?.name?.split(' ').map(w => w[0]).join('').slice(0, 2) || 'U';
  const userName = user?.name || 'User';
  const userEmail = user?.email || '';
  const userRoleLabel = getRoleLabel(user?.role);

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: '#ffffff',
        borderBottom: '1px solid',
        borderColor: 'rgba(0,0,0,0.08)',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ minHeight: 64, px: { xs: 1.5, sm: 2.5 } }}>
        {/* Menu Toggle */}
        <IconButton
          edge="start"
          onClick={onMenuToggle}
          sx={{
            mr: 1.5,
            color: PRIMARY_COLOR,
            '&:hover': { backgroundColor: alpha(PRIMARY_COLOR, 0.06) },
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Page Title */}
        <Typography
          variant="h6"
          noWrap
          sx={{
            fontWeight: 700,
            color: PRIMARY_COLOR,
            fontSize: { xs: 16, sm: 20 },
            minWidth: 'fit-content',
          }}
        >
          {pageTitle}
        </Typography>

        {/* Search Bar */}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <SearchWrapper>
            <SearchIconWrapper>
              <SearchIcon fontSize="small" />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search products, referrals..."
              inputProps={{ 'aria-label': 'search' }}
            />
          </SearchWrapper>
        </Box>

        {/* Right Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Notifications */}
          <IconButton
            onClick={handleNotifOpen}
            sx={{
              color: PRIMARY_COLOR,
              '&:hover': { backgroundColor: alpha(PRIMARY_COLOR, 0.06) },
            }}
          >
            <Badge
              badgeContent={5}
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: SECONDARY_COLOR,
                  color: '#ffffff',
                  fontSize: 11,
                  fontWeight: 700,
                  minWidth: 18,
                  height: 18,
                },
              }}
            >
              <NotificationsIcon />
            </Badge>
          </IconButton>

          <Menu
            anchorEl={notifAnchorEl}
            open={notifMenuOpen}
            onClose={handleNotifClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 280,
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PRIMARY_COLOR }}>
                Notifications
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={handleNotifClose} sx={{ py: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                New referral application received
              </Typography>
            </MenuItem>
            <MenuItem onClick={handleNotifClose} sx={{ py: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Commission payout processed
              </Typography>
            </MenuItem>
            <MenuItem onClick={handleNotifClose} sx={{ py: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Reward points earned: +150 pts
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleNotifClose} sx={{ justifyContent: 'center' }}>
              <Typography variant="body2" sx={{ color: SECONDARY_COLOR, fontWeight: 600 }}>
                View All Notifications
              </Typography>
            </MenuItem>
          </Menu>

          {/* Divider */}
          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 1, my: 1.5, borderColor: 'rgba(0,0,0,0.08)' }}
          />

          {/* User Profile */}
          <Box
            onClick={handleProfileMenuOpen}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              px: 1,
              py: 0.5,
              borderRadius: 2,
              '&:hover': { backgroundColor: alpha(PRIMARY_COLOR, 0.04) },
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                backgroundColor: SECONDARY_COLOR,
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              {userInitials}
            </Avatar>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: PRIMARY_COLOR, lineHeight: 1.2 }}
              >
                {userName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1 }}>
                {userRoleLabel}
              </Typography>
            </Box>
            <ArrowDownIcon
              sx={{ fontSize: 18, color: 'text.secondary', display: { xs: 'none', md: 'block' } }}
            />
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={profileMenuOpen}
            onClose={handleProfileMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 200,
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PRIMARY_COLOR }}>
                {userName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {userEmail}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={() => handleMenuAction('profile')} sx={{ py: 1.25 }}>
              <ListItemIcon>
                <PersonIcon fontSize="small" sx={{ color: PRIMARY_COLOR }} />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: 14 }}>Profile</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleMenuAction('settings')} sx={{ py: 1.25 }}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" sx={{ color: PRIMARY_COLOR }} />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: 14 }}>Settings</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => handleMenuAction('logout')} sx={{ py: 1.25 }}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" sx={{ color: '#e74c3c' }} />
              </ListItemIcon>
              <ListItemText
                primaryTypographyProps={{ fontSize: 14, color: '#e74c3c' }}
              >
                Logout
              </ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
